/**
 * Which channels the page should count — a dropdown holding real checkboxes.
 *
 * A DROPDOWN, NOT INLINE PILLS. Four pills beside the period selector pushed the
 * hero's action row onto a second line on a laptop and a third on a phone, which
 * is why the owner asked for a select box. One trigger the width of the period
 * selector costs a single slot, and the trigger still states the live selection —
 * which matters because this filter is REMEMBERED across pages: a reader arriving
 * with Facebook-only still on must see that without opening anything, or the page
 * looks like it is missing data.
 *
 * NOT A NATIVE `<select multiple>`: it renders as a tall always-open list box, has
 * no "all" state, and on macOS needs a modifier-click to pick a second option. The
 * mechanics here (mousedown outside, Escape, focus back to the trigger) follow
 * WorkspaceSwitcher, the dropdown this app already ships.
 *
 * Labels come from `PLATFORM_DISPLAY`, the same map the aggregation labels its
 * rows with, so a checkbox can never name a channel differently from the figures
 * it filters. Names are proper nouns and deliberately untranslated; only the
 * group's own label and the clear control go through i18n.
 *
 * @module components/ChannelFilter
 */

import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown } from "lucide-react";
import clsx from "clsx";
import { AD_CHANNEL_IDS } from "@/services/channelFilter";
import { PLATFORM_DISPLAY } from "@/services/mediaIntelligenceSummaries";
import { useChannelFilter } from "@/contexts/ChannelFilterContext";

export interface ChannelFilterProps {
  disabled?: boolean;
  className?: string;
  /**
   * Channels with no rows in the current range, so they can be marked. Not
   * hidden: a channel that vanishes from the control when it has no spend looks
   * like a bug, and a reader cannot tell "no data this period" from "we never
   * connected it". Omit to mark nothing.
   */
  emptyChannelIds?: readonly string[];
}

const nameOf = (id: string): string => PLATFORM_DISPLAY[id]?.name ?? id;

export const ChannelFilter: React.FC<ChannelFilterProps> = ({
  disabled = false,
  className,
  emptyChannelIds,
}) => {
  const { t } = useTranslation();
  const { selected, filtering, toggle, clear } = useChannelFilter();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  // Click outside closes. mousedown rather than click, so a drag that starts
  // inside and ends outside does not count as leaving.
  useEffect(() => {
    if (!open) return;
    const onDown = (event: MouseEvent): void => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  /**
   * The selection, short enough for a button.
   *
   * Beyond one channel it is "Facebook +2", not the full list: three spelled-out
   * names make the trigger wider than the four pills this replaced, which is the
   * whole problem it exists to solve.
   */
  const triggerLabel = ((): string => {
    if (!filtering) return t("common.channelAll");
    const names = [...selected].map(nameOf);
    return names.length === 1
      ? names[0]
      : t("common.channelMore", { first: names[0], more: names.length - 1 });
  })();

  return (
    <div
      ref={rootRef}
      className={clsx("relative", className)}
      // Escape closes from anywhere inside, including a focused checkbox, and
      // hands focus back to the trigger — otherwise it lands on <body> and the
      // next Tab restarts at the top of the page.
      onKeyDown={(e) => {
        if (e.key === "Escape" && open) {
          e.stopPropagation();
          setOpen(false);
          triggerRef.current?.focus();
        }
      }}
    >
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        disabled={disabled}
        aria-expanded={open}
        aria-haspopup="true"
        // The label names the control; the text inside is its value. Without this
        // a screen reader reads only "All channels", which sounds like a command.
        aria-label={t("common.channelLabel")}
        className={clsx(
          "inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm",
          "border-neutral-300 bg-white text-ink-900",
          "focus:outline-none focus:ring-2 focus:ring-ink-950 focus:border-ink-950",
          "disabled:opacity-50 disabled:cursor-not-allowed",
          // Marked on the border, not by colour alone: the figures beside it are
          // a subset of the account whenever this is on.
          filtering && "border-ink-950 font-medium",
        )}
      >
        <span className="text-neutral-700">{t("common.channelLabel")}:</span>
        <span>{triggerLabel}</span>
        <ChevronDown
          size={14}
          aria-hidden="true"
          className={clsx("text-neutral-700 transition-transform", open && "rotate-180")}
        />
      </button>

      {open && (
        <div
          role="group"
          aria-label={t("common.channelLabel")}
          className="absolute right-0 z-50 mt-2 w-56 rounded-lg border border-neutral-200 bg-white p-1 shadow-lg"
        >
          {AD_CHANNEL_IDS.map((id) => {
            // An empty selection means every channel, so every box reads ticked —
            // which is what the reader is actually looking at.
            const checked = selected.size === 0 || selected.has(id);
            const empty = emptyChannelIds?.includes(id) ?? false;
            return (
              <label
                key={id}
                className="flex cursor-pointer select-none items-center gap-2 rounded-md px-2 py-1.5 text-sm text-ink-900 hover:bg-neutral-50"
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggle(id)}
                  className="h-4 w-4 shrink-0 rounded border-neutral-300 text-ink-950 focus:ring-2 focus:ring-ink-950"
                />
                <span aria-hidden>{PLATFORM_DISPLAY[id]?.icon ?? "📱"}</span>
                <span className="flex-1 truncate">{nameOf(id)}</span>
                {/* Stated, never used to hide the row. "0" here means this period,
                    not this account. */}
                {empty && <span className="text-xs text-neutral-700">0</span>}
              </label>
            );
          })}

          {/* Only while a real subset is on — an "all channels" button under four
              ticked boxes would suggest the page is filtered when it is not. */}
          {filtering && (
            <>
              <div className="my-1 border-t border-neutral-200" />
              <button
                type="button"
                onClick={clear}
                className="w-full rounded-md px-2 py-1.5 text-left text-xs font-medium text-neutral-700 hover:bg-neutral-50 hover:text-ink-950"
              >
                {t("common.channelAll")}
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * Says the filter is on, on a page that deliberately ignores it.
 *
 * WHY /mmm GETS THIS AND NOT THE CONTROL. Every total in MMMBudgetSimulator is a
 * sum over the rows it is handed — `totalBudget`, `originalTotal`, and `change`
 * = (recommended − current) / current. The model's plan is a REALLOCATION, so
 * filtering those rows to Facebook prints "budget change −10%" for a plan that
 * moves money to TikTok and cuts nothing. A filtered model output is a different
 * claim, not a narrower view of the same one.
 *
 * But the selection is remembered across pages, so saying nothing is also wrong:
 * a reader arriving from a Facebook-only dashboard would read all-channel figures
 * as Facebook's. This states the selection, states that the page is not narrowed,
 * and offers the way out.
 */
export const ChannelFilterNotice: React.FC<{ reason: string; className?: string }> = ({
  reason,
  className,
}) => {
  const { t } = useTranslation();
  const { selected, filtering, clear } = useChannelFilter();

  if (!filtering) return null;

  const names = [...selected].map(nameOf).join(", ");

  return (
    <div
      role="status"
      className={clsx(
        "flex flex-wrap items-center gap-x-2 gap-y-1 rounded-lg border border-neutral-300 bg-white px-3 py-2 text-xs text-neutral-700",
        className,
      )}
    >
      <span className="font-medium">{t("common.channelFilterOn", { channels: names })}</span>
      <span>{reason}</span>
      <button
        type="button"
        onClick={clear}
        className="font-medium underline underline-offset-2 hover:text-ink-950"
      >
        {t("common.channelAll")}
      </button>
    </div>
  );
};

export default ChannelFilter;
